'use client'

import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import type { ChatMessage as ChatMessageType, ToolCall } from '@/context/AIContext'

type Dict = Record<string, unknown>

/** The confirm-first preview in an assistant message (last one wins), if any. */
export function pendingActionOf(message: ChatMessageType): ToolCall | null {
  if (message.role !== 'assistant' || !message.toolCalls) return null
  for (let i = message.toolCalls.length - 1; i >= 0; i--) {
    const tc = message.toolCalls[i]
    const r = tc.result as Dict | undefined
    if (r && typeof r === 'object' && r.requires_confirmation && !r.error) return tc
  }
  return null
}

const INVOICE_VERBS: Record<string, { title: (inv: string, p: Dict) => string; confirm: string }> = {
  approve: { title: (inv) => `Approve ${inv}`, confirm: 'Confirm approve' },
  reject: { title: (inv) => `Reject ${inv}`, confirm: 'Confirm reject' },
  hold: {
    title: (inv, p) => {
      const days = Number(p.hold_days)
      return Number.isFinite(days) && days > 0 ? `Hold ${inv} for ${days} day${days === 1 ? '' : 's'}` : `Hold ${inv}`
    },
    confirm: 'Confirm hold',
  },
  release: { title: (inv) => `Release ${inv}`, confirm: 'Confirm release' },
  write_off: { title: (inv) => `Write off ${inv}`, confirm: 'Confirm write-off' },
  send_for_review: { title: (inv) => `Send ${inv} for review`, confirm: 'Confirm send' },
  return_to_review: { title: (inv) => `Return ${inv} to review`, confirm: 'Confirm return' },
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : v == null ? '' : String(v)
}

/** Card title and confirm-button label for a preview. */
export function describeAction(tc: ToolCall): { title: string; confirmLabel: string } {
  const r = (tc.result ?? {}) as Dict
  const p = (r.preview ?? {}) as Dict
  const args = tc.args ?? {}
  switch (tc.name) {
    case 'update_invoice_status': {
      const verb = str(r.verb || args.action).toLowerCase().replace('-', '_')
      const inv = str(p.invoice_number || args.invoice_number) || 'this invoice'
      const known = INVOICE_VERBS[verb]
      if (known) return { title: known.title(inv, p), confirmLabel: known.confirm }
      return { title: `${str(p.action) || 'Update'} ${inv}`.trim(), confirmLabel: 'Confirm' }
    }
    case 'approve_deduction':
      return { title: `Approve deduction ${str(p.deduction_number)}`.trim(), confirmLabel: 'Confirm approve' }
    case 'reject_deduction':
      return { title: `Reject deduction ${str(p.deduction_number)}`.trim(), confirmLabel: 'Confirm reject' }
    case 'apply_payment_to_invoice':
      return {
        title: `Apply payment ${str(p.payment_reference)} to ${str(p.invoice_number)}`.replace(/\s+/g, ' ').trim(),
        confirmLabel: 'Confirm apply',
      }
    case 'create_promise_to_pay':
      return { title: `Record promise to pay from ${str(p.customer) || 'customer'}`, confirmLabel: 'Confirm promise' }
    case 'record_dunning_action':
      return { title: `Log dunning action on ${str(p.invoice_number) || 'invoice'}`, confirmLabel: 'Confirm log' }
    case 'adjust_credit_limit':
      return { title: `Change credit limit for ${str(p.customer) || 'customer'}`, confirmLabel: 'Confirm change' }
    case 'create_ai_policy':
      return { title: `Create AI Policy "${str(p.name)}"`, confirmLabel: 'Confirm create' }
    default:
      return { title: 'Confirm this action', confirmLabel: 'Confirm' }
  }
}

const FACT_LABELS: [string, string][] = [
  ['invoice_number', 'Invoice'],
  ['deduction_number', 'Deduction'],
  ['payment_reference', 'Payment'],
  ['customer_name', 'Customer'],
  ['customer', 'Customer'],
  ['name', 'Name'],
  ['amount', 'Amount'],
  ['claimed_amount', 'Claimed'],
  ['approved_amount', 'Approved'],
  ['promised_amount', 'Amount'],
  ['invoice_outstanding_before', 'Outstanding'],
  ['previous_limit', 'Current limit'],
  ['new_limit', 'New limit'],
  ['promised_date', 'Promised by'],
  ['current_status', 'Current status'],
  ['current_bucket', 'Bucket'],
  ['new_status', 'New status'],
  ['hold_days', 'Hold'],
  ['action_type', 'Action'],
  ['category', 'Category'],
  ['reason', 'Reason'],
  ['original_input', 'Rule'],
]

function formatFact(key: string, v: unknown): string {
  if (v == null || v === '') return ''
  if (key === 'hold_days') return `${v} day${Number(v) === 1 ? '' : 's'}`
  if (typeof v === 'number') return v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const s = str(v)
  if (key === 'current_status' || key === 'category' || key === 'action_type' || key === 'new_status') {
    const t = s.replace(/_/g, ' ')
    return t.charAt(0).toUpperCase() + t.slice(1)
  }
  return s
}

function factsOf(tc: ToolCall): { label: string; value: string }[] {
  const p = (((tc.result ?? {}) as Dict).preview ?? {}) as Dict
  const out: { label: string; value: string }[] = []
  const seen = new Set<string>()
  for (const [key, label] of FACT_LABELS) {
    if (!(key in p) || seen.has(label)) continue
    const value = formatFact(key, p[key])
    if (!value) continue
    seen.add(label)
    out.push({ label, value })
  }
  return out
}

interface ActionCardProps {
  toolCall: ToolCall
  state?: ChatMessageType['action']
  onConfirm: () => void
  onCancel: () => void
}

/** Confirm-first card: what will change, with Confirm and Cancel. */
export function ActionCard({ toolCall, state, onConfirm, onCancel }: ActionCardProps) {
  const status = state?.status

  if (status === 'confirmed') {
    return (
      <div className="flex items-start gap-1.5 pl-1 text-[12px] text-emerald-700">
        <Icons.checkCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
        <span>{state?.message || 'Done.'}</span>
      </div>
    )
  }
  if (status === 'cancelled') {
    return (
      <div className="flex items-start gap-1.5 pl-1 text-[12px] text-muted-foreground">
        <Icons.close className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
        <span>Cancelled. Nothing was changed.</span>
      </div>
    )
  }

  const { title, confirmLabel } = describeAction(toolCall)
  const facts = factsOf(toolCall)
  const effect = str((((toolCall.result ?? {}) as Dict).preview as Dict | undefined)?.effect)
  const running = status === 'running'
  const failed = status === 'failed'

  return (
    <div
      className={cn(
        'w-full max-w-sm rounded-xl border border-brand-cornflower/30 bg-white',
        'shadow-soft overflow-hidden'
      )}
      role="group"
      aria-label={title}
    >
      <div className="flex items-center gap-2 border-b border-brand-cornflower/20 bg-brand-cornflower/5 px-3 py-2">
        <Icons.zap className="h-3.5 w-3.5 text-brand-cornflower" strokeWidth={1.5} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-cornflower">Action</span>
      </div>
      <div className="px-3 py-2.5">
        <p className="text-sm font-semibold text-brand-navy">{title}</p>
        {facts.length > 0 && (
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
            {facts.map((f) => (
              <div key={f.label} className="contents">
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="truncate text-foreground" title={f.value}>{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
        {effect && <p className="mt-2 text-[11px] text-muted-foreground">{effect.replace(/→/g, 'to')}</p>}
        {failed && (
          <div className="mt-2 flex items-start gap-1.5 text-[11px] text-red-600">
            <Icons.alertCircle className="mt-px h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} />
            <span>{state?.message || 'The action could not be completed. Nothing was changed.'}</span>
          </div>
        )}
        {!failed && (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={onConfirm}
              disabled={running}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5',
                'bg-brand-navy text-xs font-medium text-white',
                'transition-colors hover:bg-brand-navy/90 disabled:opacity-60'
              )}
            >
              {running && <Icons.loader className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
              {running ? 'Working...' : confirmLabel}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={running}
              className={cn(
                'rounded-lg border border-border/60 bg-white px-3 py-1.5',
                'text-xs font-medium text-muted-foreground',
                'transition-colors hover:bg-muted/40 hover:text-foreground disabled:opacity-60'
              )}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
